/**
 * "Điều chỉnh số dư" (owner decision 2026-10-02): staff credit ("Cộng tiền") or debit ("Trừ
 * tiền") a customer's wallet with a reason and the transaction code (required; a code recorded
 * before is credited only once staff tick that they checked it; above 50 000 000 đ a second
 * person approves first — owner decision 2026-10-05 §3, §4). A credit pays the customer's orders
 * waiting for funds. Shown only with a platform-wide `billing.adjust` grant; the server also needs an MFA-verified session and
 * refuses a debit above the available balance. One Idempotency-Key per distinct request
 * (`useWrite`), and the operator ticks a confirmation naming the amount and the customer.
 */

import { Scale } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { CustomerBilling } from '../../api/areas/accounts';
import { useSession } from '../../api/session';
import { Callout, Field } from '../../components/blocks';
import { CustomerScope } from '../../components/NotifyCustomer';
import { useWrite, WriteError } from '../../components/Write';
import { useAccess } from '../../lib/access';
import { formatMoney } from '../../lib/format';
import { adjustmentRequest, amountText, LARGE_ADJUSTMENT_VND, mayAdjustWallet, needsSecondApproval, type AdjustDirection, type AdjustDraft, type WalletAdjustmentRequest } from '../../lib/walletAdjustment';
import { DuplicateReference, duplicateMatches } from '../commerce/DuplicateReference';
import { journalPath } from '../commerce/shared';
import { ConfirmCheck, MfaHint } from '../compliance/ui';
import { ActionDialog, FormFooter, ReasonField, type DialogBodyProps } from '../operator/shared';

type Wallet = CustomerBilling['wallets'][number];

interface Customer {
  id: string;
  name: string;
  /** The billing currency: the wallet orders are paid from. */
  currency: string;
}

const DIRECTIONS: AdjustDirection[] = ['CREDIT', 'DEBIT'];

function AdjustForm({ customer, wallet, close, changed }: DialogBodyProps & { customer: Customer; wallet: Wallet | undefined }) {
  const { t } = useTranslation('accounts');
  const session = useSession();
  const [d, setD] = useState<AdjustDraft>({ direction: 'CREDIT', amount: '', currency: customer.currency, reason: '', reference: '' });
  const [confirmed, setConfirmed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const w = useWrite((b: WalletAdjustmentRequest, opts) => api.adjustCustomerWallet(customer.id, b, opts));
  // Any change asks for the confirmation again and clears a refusal of the previous request.
  const set = (patch: Partial<AdjustDraft>) => {
    setD((x) => ({ ...x, ...patch }));
    setConfirmed(false);
    setAcknowledged(false);
    if (w.state.phase === 'error') w.reset();
  };
  const money = (amount: string) => formatMoney({ amount, currency: customer.currency });
  const done = w.state.phase === 'done' ? w.state.result : null;
  if (done && 'status' in done) {
    return (
      <div className="grid gap-3">
        <Callout tone="info">{t('walletAdjust.requested')}</Callout>
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }
  if (done) {
    return (
      <div className="grid gap-3">
        <Callout tone="ok">
          {t('walletAdjust.done', { balance: money(done.balance), available: money(done.available) })}{' '}
          <Link to={journalPath(done.journal_entry_id)} className="font-medium text-primary underline-offset-4 hover:underline">
            {t('walletAdjust.openEntry')}
          </Link>
        </Callout>
        {done.orders_funded.length ? <Callout tone="ok">{t('walletAdjust.ordersFunded', { count: done.orders_funded.length })}</Callout> : null}
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }
  const duplicates = w.state.phase === 'error' ? duplicateMatches(w.state.error) : null;
  const built = adjustmentRequest(d);
  const request = 'request' in built ? { ...built.request, acknowledge_duplicate: acknowledged } : null;
  const amountWrong = d.amount.trim() !== '' && 'problem' in built && built.problem === 'amount';
  const amountTooLarge = 'problem' in built && built.problem === 'amountTooLarge';
  const magnitude = request?.amount.replace(/^-/, '');
  const amountHelp = amountText(customer.currency);
  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (request && confirmed) void w.run(request).then((r) => r && changed());
      }}
    >
      {wallet ? (
        <p className="text-sm text-muted-foreground">{t('walletAdjust.current', { balance: money(wallet.balance), held: money(wallet.held), available: money(wallet.available) })}</p>
      ) : (
        <Callout tone="info">{t('walletAdjust.noWallet', { currency: customer.currency })}</Callout>
      )}
      <Field label={t('walletAdjust.direction')} group>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('walletAdjust.direction')}>
          {DIRECTIONS.map((x) => (
            <Button key={x} type="button" size="sm" variant={d.direction === x ? 'default' : 'outline'} aria-pressed={d.direction === x} onClick={() => set({ direction: x })}>
              {t(x === 'CREDIT' ? 'walletAdjust.credit' : 'walletAdjust.debit')}
            </Button>
          ))}
        </div>
      </Field>
      <Field
        label={t('walletAdjust.amount', { currency: customer.currency })}
        hint={
          amountWrong ? (
            <span className="text-critical">{t(amountHelp.invalid, amountHelp.values)}</span>
          ) : amountTooLarge ? (
            <span className="text-critical">{t('walletAdjust.amountTooLarge')}</span>
          ) : d.direction === 'DEBIT' && wallet ? t('walletAdjust.debitLimit', { available: money(wallet.available) }) : t(amountHelp.hint, amountHelp.values)
        }
      >
        <Input inputMode="decimal" autoComplete="off" value={d.amount} aria-invalid={amountWrong || amountTooLarge} onChange={(e) => set({ amount: e.target.value })} />
      </Field>
      <ReasonField value={d.reason} onChange={(reason) => set({ reason })} />
      <Field label={t('walletAdjust.reference')} hint={t('walletAdjust.referenceHint')}>
        <Input value={d.reference} maxLength={100} autoComplete="off" required onChange={(e) => set({ reference: e.target.value })} />
      </Field>
      {request && needsSecondApproval(request) ? <Callout tone="info">{t('walletAdjust.large', { threshold: formatMoney({ amount: String(LARGE_ADJUSTMENT_VND), currency: 'VND' }) })}</Callout> : null}
      {request && magnitude ? (
        <ConfirmCheck checked={confirmed} onChange={setConfirmed}>
          {t(d.direction === 'DEBIT' ? 'walletAdjust.confirmDebit' : 'walletAdjust.confirmCredit', { amount: money(magnitude), customer: customer.name })}
        </ConfirmCheck>
      ) : null}
      {session?.mfa_verified ? null : <MfaHint permission="billing.adjust" />}
      {duplicates ? (
        <DuplicateReference matches={duplicates} currency={customer.currency} checked={acknowledged} onChange={setAcknowledged} />
      ) : w.state.phase === 'error' ? (
        <WriteError error={w.state.error} />
      ) : null}
      <FormFooter busy={w.busy} disabled={!request || !confirmed || (duplicates !== null && !acknowledged)} submit={t('walletAdjust.submit')} onClose={close} danger={d.direction === 'DEBIT'} />
    </form>
  );
}

/**
 * "Điều chỉnh số dư" on the customer's wallet (nothing without a platform-wide `billing.adjust` grant). No customer
 * webhook choice: an adjustment sends none.
 */
export function WalletAdjustAction({ customer, wallet, onChanged }: { customer: Customer; wallet: Wallet | undefined; onChanged: () => void }) {
  const { t } = useTranslation('accounts');
  const access = useAccess();
  if (!mayAdjustWallet(access.grants, access.client)) return null;
  return (
    <CustomerScope customerId={null}>
      <ActionDialog label={t('walletAdjust.action')} icon={Scale} title={t('walletAdjust.title')} description={t('walletAdjust.description', { customer: customer.name })} onChanged={onChanged}>
        {(p) => <AdjustForm customer={customer} wallet={wallet} {...p} />}
      </ActionDialog>
    </CustomerScope>
  );
}
